import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetUserProfileMinimalQuery } from '@/generated/graphql';
import { useNotification } from '@/common/components';
import { useAuth } from '@/common/auth/hooks/useAuth';
import { LoadingState } from '@/common/components/loading/LoadingState';
import { isPublicPath } from '@/routes/publicPaths';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError } = useNotification();

  // If not authenticated and not loading, redirect to login immediately
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !isPublicPath(location.pathname)) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, location.pathname, navigate]);

  // Only run the profile query if authenticated
  const {
    loading: profileLoading,
    error: profileError,
    data: profileData,
    refetch: refetchProfile,
  } = useGetUserProfileMinimalQuery({
    fetchPolicy: 'network-only',
    skip: authLoading || !isAuthenticated,
    errorPolicy: 'all',
  });

  const isLoading = authLoading || (isAuthenticated && profileLoading);

  // Show error notification when profile fetch fails
  useEffect(() => {
    if (profileError) {
      console.error('Profile fetch error:', profileError);
      showError('Failed to load profile data');
      // If authenticated but query failed, try to refetch once
      if (isAuthenticated && !authLoading) {
        setTimeout(() => {
          refetchProfile().catch((err) => {
            console.error('Profile refetch failed:', err);
          });
        }, 1000);
      }
    }
  }, [profileError, showError, isAuthenticated, authLoading, refetchProfile]);

  // Refetch profile when authentication state changes to authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading && !profileLoading && !profileData) {
      refetchProfile().catch((err) => {
        console.error('Initial profile fetch failed:', err);
      });
    }
  }, [
    isAuthenticated,
    authLoading,
    profileLoading,
    profileData,
    refetchProfile,
  ]);

  useEffect(() => {
    // Skip protection for public pages
    if (isPublicPath(location.pathname)) {
      return;
    }

    // Handle register page: requires authentication but allows missing profile data
    if (location.pathname === '/register') {
      if (!isAuthenticated && !authLoading) {
        navigate('/login', { replace: true });
        return;
      }
      // If authenticated and has complete profile, redirect to projects
      const hasCompleteProfile = profileData?.user?.artistName;
      if (hasCompleteProfile && !isLoading) {
        navigate('/projects', { replace: true });
      }
      return;
    }

    // For all other pages: require full authentication and complete profile
    if (!isAuthenticated && !authLoading) {
      navigate('/login', { replace: true });
      return; // Early return to prevent further checks
    }

    // Only check profile data if user is authenticated
    // If the user is missing critical profile data, redirect to register page
    const isMissingCriticalData = !profileData?.user?.artistName;
    if (isMissingCriticalData && !isLoading && isAuthenticated) {
      navigate('/register', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    location.pathname,
    navigate,
    profileData,
    profileLoading,
    authLoading,
    isLoading,
  ]);

  // Show loading screen while checking authentication or profile
  if (isLoading) {
    const messages: string[] = [];
    if (authLoading) messages.push('Loading...');
    if (profileLoading) messages.push('Loading profile...');
    return (
      <LoadingState
        size={60}
        message={messages.length > 0 ? messages : undefined}
        fullPage
      />
    );
  }

  return <>{children}</>;
};
